/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Color } from "@packages/ui";
import {
  colors,
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  radius,
  spacing,
  Text,
  useThemeValues,
} from "@packages/ui";

const containerStyles: Interpolation = {
  borderRadius: radius.m,
  flexGrow: 1,
  flexShrink: 0,
  maxWidth: `calc(100vw - ${spacing.l})`,
  overflow: "hidden",
  width: spacing.full,
  [cssMediaQueryMinWidthTablet]: {
    width: "30%",
  },
};

const imageContainerStyles: Interpolation = {
  height: "280px",
};

const centerImageContainerStyles: Interpolation = {
  paddingTop: spacing.xl,
};

const imageStyles: Interpolation = {
  height: spacing.full,
  objectPosition: "center",
  width: spacing.full,
};

const centerImageStyles: Interpolation = {
  objectFit: "contain",
};

const coverImageStyles: Interpolation = {
  objectFit: "cover",
};

const textStyles: Interpolation = {
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  paddingBottom: spacing.l,
  paddingInline: spacing.l,
  paddingTop: spacing.m,
  rowGap: spacing.xxs,
  textAlign: "start",
};

const titleStyles: Interpolation = {
  color: constantSemanticColors.fixedLightenHighest,
};

const descriptionStyles: Interpolation = {
  color: constantSemanticColors.fixedLightenVeryHigh,
};

type BravadoProps = {
  image: string;
  imageStyle?: "cover" | "center";
  imageBackground: Color;
  textBackground?: Color;
  title: string;
  description: string;
};

export const Bravado = ({
  image,
  imageBackground,
  imageStyle = "center",
  textBackground = imageBackground,
  title,
  description,
}: BravadoProps) => {
  const theme = useThemeValues();

  const resolveColor = (color: Color): string => {
    if (color in colors)
      return (colors as Record<string, string>)[color as string];
    if (color in theme.semanticColors)
      return theme.semanticColors[color as keyof typeof theme.semanticColors];
    return color as string;
  };

  return (
    <div css={containerStyles}>
      <div
        css={[
          { backgroundColor: resolveColor(imageBackground) },
          imageStyle === "center" && centerImageContainerStyles,
          imageContainerStyles,
        ]}
      >
        <img
          src={image}
          alt={""}
          css={[
            imageStyles,
            imageStyle === "cover" ? coverImageStyles : centerImageStyles,
          ]}
        />
      </div>
      <div
        css={[{ backgroundColor: resolveColor(textBackground) }, textStyles]}
      >
        <Text as={"h3"} type={"title"} size={"xl"} css={titleStyles}>
          {title}
        </Text>
        <Text as={"p"} type={"body"} size={"m"} css={descriptionStyles}>
          {description}
        </Text>
      </div>
    </div>
  );
};
